/**
 * Options for computing the vertical placement of a dropdown list relative to
 * a trigger element, as used by `fx-select`.
 */
export interface DropdownPlacementOptions {
  triggerRect: DOMRect | DOMRectReadOnly;
  popoverHeight: number;
  viewportHeight: number;
}

/**
 * Returns whether the dropdown should be rendered above or below the trigger.
 * Flips to `"top"` when there is not enough room below and there is more room
 * above.
 */
export function computeDropdownPlacement(
  options: DropdownPlacementOptions
): "bottom" | "top" {
  const { triggerRect, popoverHeight, viewportHeight } = options;
  const spaceBelow = viewportHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;

  return spaceBelow < popoverHeight && spaceAbove > spaceBelow ? "top" : "bottom";
}

/**
 * Options for fitting a popover (context menu, sub-menu) inside the viewport at
 * an anchor point.
 */
export interface PopoverFitOptions {
  anchorX: number;
  anchorY: number;
  popoverWidth: number;
  popoverHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  /**
   * Preferred opening direction. Defaults to right/bottom (typical for context
   * menus and sub-menus).
   */
  preferredX?: "left" | "right";
  preferredY?: "top" | "bottom";
  gap?: number;
}

/**
 * Fit a popover inside the viewport, flipping horizontally and/or vertically
 * when the preferred direction would overflow. Returns the coordinates and the
 * final placement axes.
 */
export function fitPopoverInViewport(
  options: PopoverFitOptions
): {
  x: number;
  y: number;
  placementX: "left" | "right";
  placementY: "top" | "bottom";
} {
  const {
    anchorX,
    anchorY,
    popoverWidth,
    popoverHeight,
    viewportWidth,
    viewportHeight,
    preferredX = "right",
    preferredY = "bottom",
    gap = 0,
  } = options;

  let x = anchorX;
  let y = anchorY;
  let placementX = preferredX;
  let placementY = preferredY;

  if (preferredX === "right") {
    if (x + popoverWidth + gap > viewportWidth && x - popoverWidth >= 0) {
      x = x - popoverWidth;
      placementX = "left";
    }
  } else if (preferredX === "left") {
    if (x - popoverWidth < 0 && x + popoverWidth + gap <= viewportWidth) {
      x = x + popoverWidth;
      placementX = "right";
    }
  }

  if (preferredY === "bottom") {
    if (y + popoverHeight + gap > viewportHeight && y - popoverHeight >= 0) {
      y = y - popoverHeight;
      placementY = "top";
    }
  } else if (preferredY === "top") {
    if (y - popoverHeight < 0 && y + popoverHeight + gap <= viewportHeight) {
      y = y + popoverHeight;
      placementY = "bottom";
    }
  }

  // Final clamp so the popover never overflows on the opposite axis.
  x = Math.max(0, Math.min(x, viewportWidth - popoverWidth));
  y = Math.max(0, Math.min(y, viewportHeight - popoverHeight));

  return { x, y, placementX, placementY };
}
